import { LitElement, html, css } from 'lit-all'
import styles from 'lib-styles'
import { StorefrontClient, formatMoney, updateCartQuantity, t } from 'lib-utils'

class NovaCartItem extends LitElement {
  static properties = {
    item: {
      type: Object,
    },
    compareAtPrice: {
      type: String,
    },
    cartData: {
      type: Object,
    },
    compareAtPriceValue: {
      type: String,
    },
    priceOff: {
      type: String,
    },
    reachLimitInventoryText: {
      type: String,
    },
  }

  constructor() {
    super()
    this.item = {}
    this.cartData = {}
    this.compareAtPriceValue = ''
    this.priceOff = ''
    this.reachLimitInventoryText = ''
  }

  async connectedCallback() {
    super.connectedCallback()
    // Removed duplicate price update since it's handled in updated()
  }

  get formattedFinalPrice() {
    if (!this.item.final_price) return ''
    return formatMoney(this.item.final_price * this.item.quantity)
  }

  get formattedOriginalPrice() {
    if (
      !this.item.final_price ||
      !this.item.original_price ||
      Number(this.item.original_price) === Number(this.item.final_price)
    )
      return ''
    return formatMoney(this.item.original_price * this.item.quantity)
  }

  async _fetchComparedAtPrice(id) {
    
    try {
      const storefrontClient = new StorefrontClient()
      const productData = await storefrontClient.getVariantSalePriceById(
        id,
        this.cartData.country_code
      )

      if (!productData) {
        this.compareAtPriceValue = ''
        return
        
      }
      if (productData.compareAtPrice) {
        const comparedAtPrice =
          Number(productData.compareAtPrice.amount) * 100 * this.item.quantity
        this.compareAtPriceValue = comparedAtPrice
        this._renderPriceOff()
      } else {
        this.compareAtPriceValue = ''
      }
    } catch (error) {
      console.error('Error fetching product:', error)
      this.compareAtPriceValue = ''
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('item')) {
      this._fetchComparedAtPrice(this.item.variant_id)
    }
  }

  _renderPriceOff() {
    if (
      !this.item.final_price ||
      !this.item.original_price ||
      this.compareAtPriceValue === ''
    ) {
      this.priceOff = ''
      return
    }
    const originalPrice =
      this.compareAtPriceValue !== ''
        ? this.compareAtPriceValue
        : this.item.original_price
    const salePrice = originalPrice - this.item.final_price * this.item.quantity
    const percentageOff = ((salePrice / originalPrice) * 100).toFixed(0)

    this.priceOff = percentageOff
  }

  _renderOptions() {
    return this.item.options_with_values.map(
      (option) => html` <div class="cart-item__options flex">
        <dt>${option.name}:</dt>
        <dd>${option.value}</dd>
      </div>`
    )
  }

  _renderProperties() {
    return Object.keys(this.item.properties).map(
      (key) => html`${
        key[0] != '_' ? html`
          <div class="cart-item__options flex">
            <dt>${key}:</dt>
            <dd>${this.item.properties[key]}</dd>
          </div>
        ` : ``
      }`
    )
  }

  _handleQuantityChange = (event) => {
    this.updateQuantity(event.detail.value)
  }

  async updateQuantity(quantity) {
    this.loading = true
    await updateCartQuantity(
      [
        {
          id: this.item.key,
          quantity,
        },
      ],
      false
    )
    this.loading = false
    document.dispatchEvent(new CustomEvent('nova:changeQuantityFinished'), {
      bubbles: true,
    })
  }

  render() {
    return html`
      <div class="cart-item__media">
        <a href="${this.item.url}">
          ${this.item.image
            ? html`<img
                class="cart-item__image cover"
                src="${this.item.image}"
                alt="${this.item.product_title}"
                width="120px"
              />`
            : ''}
        </a>
      </div>
      <span class="cart-item__details">
        <a class="cart-item__title" href="${this.item.url}">
        ${this.item.product_title}
        </a>
        <div class="cart-item__prices">
          ${this.item.final_price * this.item.quantity <
          this.compareAtPriceValue
            ? html`<div class="cart-item__discounted-prices">
                <div class="cart-item__sale-price-container">
                  <span class="visually-hidden">${t('products.product.price.sale_price')}</span>
                  <span class="cart-item__sale-price"
                    >${this.formattedFinalPrice}</span
                  >
                </div>
                <div class="cart-item__compare-at-price-container">
                  <span class="visually-hidden">${t('products.product.price.regular_price')}</span>
                  <span class="cart-item__compare-at-price">
                    ${this.compareAtPriceValue !== ''
                      ? formatMoney(this.compareAtPriceValue)
                      : this.formattedOriginalPrice !== ''
                      ? this.formattedOriginalPrice
                      : ''}
                  </span>
                </div>
                ${this.priceOff !== ''
                  ? html`
                      <div class="cart-item__price-off-container">
                        <span class="visually-hidden">${t('products.product.price.amount_saved')}</span>
                        <span class="cart-item__price-off">
                          (${this.priceOff}% off)
                        </span>
                      </div>
                    `
                  : ''}
              </div>`
            : html`<span>${this.formattedFinalPrice} </span>`}
        </div>
        <div class="cart-item__props flex column">
        ${this.item.options_with_values ? this._renderOptions() : ''}
        ${this.item.properties ? this._renderProperties() : ''}
        </div>
      </span>
      <sl-button
        variant="text"
        class="cart-item__remove"
        @click=${() => this.updateQuantity(0)}
      >
        Remove
      </sl-button>
      <nova-quantity-input
        initial=${this.item.quantity}
        @changeQuantityStarted=${this._handleQuantityChange}
        class="cart-item__quantity-input"
        type="mini-cart"
        variant-max=${this.item.inventory_quantity}
        data-template="mini-cart"
        data-reach-limit-text="${this.reachLimitInventoryText}"
      ></nova-quantity-input>
    `
  }

  static styles = [
    styles.flex,
    styles.a11y,
    css`
      :host {
        width: 100%;
        margin-bottom: 12px;
        display: grid;
        grid-template-columns: 42.1% auto 100px;
        grid-template-rows: auto 44px;
        grid-template-areas:
          'media details details'
          'media remove quantity';
      }
          .cart-item__props {gap:2px; flex-direction: column;}

      .cart-item__media {
        grid-area: media;
        display: flex;
        aspect-ratio: 4/5;
        padding-right: 12px;
      }
      .cart-item__media a {
        width: 100%;
        display: block;
        aspect-ratio: 4/5;

        align-items: center;
      }
      .cart-item__media img {
        width: 100%;
        height: auto;
        object-fit: cover;
        border-radius: 5px;
        aspect-ratio: 4/5;
      }
      .cart-item__details {
        grid-area: details;
        font-size: 14px;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .cart-item__details a {
        color: var(--nova-color-text);
        text-decoration: none;
        text-transform: capitalize;
      }
      .cart-item__title {
        font-size: 16px;
        font-weight: 600;
        line-height: 100%;
      }
      .cart-item__options {
        text-transform: capitalize;
        line-height: 120%;
      }
      .cart-item__options dd {
        margin-left: 5px;
      }
      .cart-item__prices .visually-hidden {
        visibility: hidden;
        display: none;
      }
      .cart-item__discounted-prices {
        margin: 0;
        display: flex;
        gap: 4px;
      }
      .cart-item__prices {
        font-size: 14px;
        line-height: 120%;
      }
      .cart-item__sale-price {
        color: var(--nova-utility-sale, #c00);
        font-weight: 600;
      }
      .cart-item__compare-at-price {
        color: var(--nova-brand-subdued);
        font-size:80%;
        opacity: .8;
        text-decoration: line-through;
      }
      .cart-item__price-off-container {
        color: var(--nova-brand-subdued);
        
      }
    .cart-item__remove {
        grid-area: remove;
      }
      .cart-item__remove::part(base) {
        color: var(--nova-color-text);
        text-decoration: underline;
        opacity:.7;
        text-underline-offset: 2px;
        font-size: 13px;
        font-style: normal;
        font-weight: 400;
        line-height: 120%;
        width: min-content;
        display: flex;
        align-items: center;
      }
      .cart-item__remove::part(label) {
        padding: 0;
      }
      .cart-item__quantity-input {
        grid-area: quantity;
        display: flex;
        justify-content: end;
      }
      @media (min-width: 768px) {
        :host {
          margin-bottom: 16px;
          grid-template-columns: 150px auto 100px;
        }
        .cart-item__media {
          padding-right: 16px;
        }
        .cart-item__media img {
          aspect-ratio:4/5;
        }
        .cart-item__title {
          font-size: 20px;
        }
        .cart-item__prices {
          font-size: 16px;
        }
        .cart-item__sale-price {
          padding-right: 4px;
        }
      }
    `,
  ]
}

customElements.define('nova-cart-item', NovaCartItem)
